import React from "react";
import { FiBox, FiPackage, FiCalendar, FiClock } from "react-icons/fi";

const getDecisionBadge = (status) => {
  switch (status) {
    case "Return_Request_Approved":
      return { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Approved" };
    case "Return_Request_Rejected":
      return { bg: "bg-red-50 text-red-700 border-red-200", label: "Rejected" };
    case "Return_Request_Pending":
    default:
      return { bg: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending" };
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
};

const SupplierReturnListItem = ({ item, isActive, onClick }) => {
  const decision = getDecisionBadge(item.returnDecision?.status);
  const isDispatched = item.returnDispatch?.status === "Dispatched";
  const isDelivered = item.returnDispatch?.status === "Delivered";
  const isPartial = item.returnType !== "Full_Order";

  const orderNumber =
    typeof item.orderId === "object" ? item.orderId?.orderNumber : item.orderId || "—";
  const quotationNumber =
    typeof item.quotationId === "object"
      ? item.quotationId?.quotationUniqueNumber || item.quotationId?.quotationNumber
      : item.quotationId || "—";

  return (
    <div
      onClick={onClick}
      className={`group relative rounded-xl border cursor-pointer transition-all duration-200 overflow-hidden ${isActive
          ? "border-[#772037] bg-[#772037]/[0.03] shadow-md shadow-[#772037]/10"
          : "border-gray-200 bg-white hover:border-[#772037]/40 hover:shadow-sm"
        }`}
    >
      {/* Active left accent bar */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl transition-all duration-200 ${isActive ? "bg-gradient-to-b from-[#772037] to-[#CC234F]" : "bg-transparent group-hover:bg-[#772037]/20"
          }`}
      />

      <div className="pl-3 pr-3 pt-3 pb-3 ml-1 space-y-2.5">
        {/* Top: Case ID & Badges */}
        <div className="flex items-center justify-between gap-1 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? "text-[#772037]" : "text-gray-400"}`}>
            {item.uniqueCaseId || `#${(item._id || "").slice(-6).toUpperCase()}`}
          </span>

          <div className="flex items-center gap-1">
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${decision.bg}`}>
              {decision.label}
            </span>

            {(isDispatched || isDelivered) && (
              <span
                className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${isDelivered
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-blue-50 text-blue-700 border-blue-200"
                  }`}
              >
                {isDelivered ? "Delivered" : "In Transit"}
              </span>
            )}
          </div>
        </div>

        {/* Middle: Icon & Order Info */}
        <div className="flex items-start gap-2.5">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center text-base flex-shrink-0 border ${isActive
                ? "bg-[#772037]/10 text-[#772037] border-[#772037]/20"
                : "bg-gray-50 text-gray-600 border-gray-100"
              }`}
          >
            {isPartial ? <FiBox /> : <FiPackage />}
          </div>

          <div className="flex-1 min-w-0">
            <h4 className={`font-bold text-xs leading-tight truncate ${isActive ? "text-[#772037]" : "text-gray-800"}`}>
              {orderNumber}
            </h4>

            {quotationNumber !== "—" && (
              <p className="text-[11px] text-gray-500 font-medium truncate mt-0.5">
                Quote: {quotationNumber}
              </p>
            )}

            <div className="flex items-center gap-1 mt-1 flex-wrap">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[9px] font-bold bg-gray-100 text-gray-600 border border-gray-200">
                {isPartial ? "Partial" : "Full Order"}
                {isPartial && item.packSpecification?.numberOfPacksToBeReturned && (
                  <span>({item.packSpecification.numberOfPacksToBeReturned} pk)</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Reason snippet */}
        {item.reason && (
          <p className="text-[11px] text-gray-500 line-clamp-1 bg-gray-50 px-2 py-1 rounded-lg border border-gray-100">
            <strong className="text-gray-600 font-semibold">Reason:</strong> {item.reason}
          </p>
        )}

        {/* Divider */}
        <div className="border-t border-dashed border-gray-100" />

        {/* Bottom meta row */}
        <div className="flex items-center justify-between text-[10px] text-gray-400">
          <span className="flex items-center gap-1">
            <FiCalendar className="text-[10px]" /> {formatDate(item.createdAt)}
          </span>
          <span className="font-bold text-[#772037] group-hover:underline">
            View Details →
          </span>
        </div>
      </div>
    </div>
  );
};

export default SupplierReturnListItem;
